<template>
    <div class="login-card">
        <div id="preloader" v-if="load" class="preloader"></div>
        <div class="login-card-body">
            <div class="login-head"><span class="login-text">Log In</span></div>
            <div class="inputs-group">
                <div class="input">
                    <label class="input-label" for="email">Email *</label>
                    <input
                        class="input-enter"
                        required
                        placeholder="Email"
                        type="email"
                        @blur="$v.email.$touch"
                        v-model="$v.email.$model"
                        id="email"
                    />
                    <span
                        class="error-form-message"
                        v-if="$v.email.$dirty && !$v.email.required"
                    >
                        Email field is required.
                    </span>
                    <span
                        class="error-form-message"
                        v-else-if="$v.email.$dirty && !$v.email.email"
                    >
                        Please enter a valid email.
                    </span>
                    <span class="error-form-message" v-else-if="emailError">
                        {{ emailError }}
                    </span>
                </div>
                <div class="input">
                    <label class="input-label" for="pass">
                        <span class="label">Password *</span>
                    </label>
                    <input
                        class="input-enter"
                        placeholder="Password"
                        :type="passwordVisible ? 'text' : 'password'"
                        id="pass"
                        @blur="$v.password.$touch"
                        v-model.trim="$v.password.$model"
                    />
                    <i
                        class="fa"
                        :class="passwordVisible ? 'fa-eye' : 'fa-eye-slash'"
                        aria-hidden="true"
                        @click="togglePasswordVisibility"
                    ></i>
                    <span
                        class="error-form-message"
                        v-if="$v.password.$dirty && !$v.password.required"
                    >
                        Password field is required.
                    </span>
                    <span class="error-form-message" v-else-if="passwordError">
                        {{ passwordError }}
                    </span>
                </div>
                <div class="enter-links">
                    <div>
                        <a class="link" href="password/reset-by-email">
                            <span class="enter-link">Forgot your password?</span>
                        </a>
                    </div>
                    <div>
                        <a class="link" href="register">
                            <span class="enter-link">
                                New to Hedlyner? Create your account here
                            </span>
                        </a>
                    </div>
                </div>
            </div>
            <button class="call-to-action-btn" @click="getAuth">
                <span class="call-to-action-text">Log In</span>
            </button>
            <login-footer-vue></login-footer-vue>
        </div>
    </div>
</template>

<script>
    import useNotification from '@/composables/useNotification.js'
    import LoginFooterVue from './parts/FooterLogin.vue'
    import axios from 'axios'
    import { required, email } from 'vuelidate/lib/validators'
    export default {
        name: 'RegularLogin',
        components: { LoginFooterVue },
        validations: {
            email: { required, email },
            password: { required }
        },
        setup() {
            const { notification } = useNotification()

            return { notification }
        },
        data() {
            return {
                email: '',
                emailError: '',
                emailValidate: '',
                password: '',
                passwordError: '',
                passwordValidate: '',
                load: false,
                passwordVisible: false
            }
        },
        watch: {
            email: function () {
                this.resetServerError('email')
            },
            password: function () {
                this.resetServerError('password')
            }
        },
        methods: {
            async getAuth() {
                if (this.formValid()) {
                    try {
                        this.load = true
                        await axios.post('/login', {
                            email: this.email,
                            password: this.password
                        })
                        await this.checkIfVenue()
                    } catch (error) {
                        if (error.response) {
                            const { email, password } = error.response.data.errors
                            this.emailError = email ? email[0] : ''
                            this.passwordError = password ? password[0] : ''

                            this.notification(
                                'error',
                                'Authentication failed. Please check your credentials.'
                            )
                        } else {
                            this.notification(
                                'error',
                                'An error occurred while processing your request.'
                            )
                        }
                    } finally {
                        this.load = false
                    }
                }
            },
            async checkIfVenue() {
                const userResp = await this.fetchUserData()
                const bookingVenue = userResp.data.data[0].bookingVenue
                const bookingArtist = userResp.data.data[0].bookingArtist
                if (bookingVenue) {
                    this.redirectToBookingManager()
                } else if (bookingArtist) {
                    this.redirectToArtistBookingManager()
                } else {
                    await this.checkEmail()
                }
            },

            async checkEmail() {
                const userResp = await this.fetchUserData()
                const isVerifiedEmail = userResp.data.data[0].isVerifiedEmail

                if (isVerifiedEmail) {
                    this.redirectToProfiles()
                } else {
                    this.redirectToEmailVerification()
                }
            },
            async fetchUserData() {
                const userResp = await axios.get('/profile/v1/settings/user')
                return userResp
            },
            formValid() {
                this.$v.$touch()
                return !this.$v.$invalid
            },
            resetServerError(field) {
                const serverErrorState = field + 'Error'

                if (this[serverErrorState]) {
                    this[serverErrorState] = ''
                }
            },
            redirectToBookingManager() {
                window.location.href = '/booking-manager'
            },
            redirectToArtistBookingManager() {
                window.location.href = '/booking-manager/artist'
            },

            redirectToProfiles() {
                window.location.href = '/profiles'
            },

            redirectToEmailVerification() {
                window.location.href = '/email/verify'
            },

            togglePasswordVisibility() {
                this.passwordVisible = !this.passwordVisible
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import '../../../sass/_variables.scss';
    .fa {
        color: #acacac;
        position: absolute;
        right: 10px;
        top: 55%;
    }
    .error-form-message {
        color: #ed3c0d;
        font-family: Instrument Sans;
        font-size: 10px;
        font-style: normal;
        font-weight: 600;
        line-height: 130%;
        letter-spacing: 0.14px;
    }
    .login-card {
        justify-content: center;
        margin: 70px auto 0px;
        max-width: 60%;
        min-width: 60%;
        padding: 40px 0px;
        max-width: 625px;
        min-width: 280px;
        width: 100%;
    }

    .login-card-body {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        align-items: center;
        border-radius: 15px;
        padding: min(48px, 32px) min(80px, 16px);
        background: #262626;
        gap: 48px;
    }

    .login-head {
        color: #dedede;
        font-family: Instrument Sans;
        font-size: 44px;
        font-style: normal;
        font-weight: 500;
        line-height: 110%;
        letter-spacing: -1.76px;
    }

    .inputs-group {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 20px;
        align-self: stretch;
        max-width: 550px;
        min-width: 280px;
        width: 100%;
        margin-left: auto;
        margin-right: auto;
    }

    .input {
        display: flex;
        flex-direction: column;
        gap: 8px;
        align-self: stretch;
        position: relative;
        max-width: 550px;
        min-width: 280px;
        width: 100%;
        margin-left: auto;
        margin-right: auto;
    }

    .input-label {
        color: #8b8b8b;
        font-family: Instrument Sans;
        font-size: 12px;
        font-style: normal;
        font-weight: 700;
        line-height: 135%;
        letter-spacing: 0.12px;
    }

    .input-enter {
        display: flex;
        align-self: stretch;
        border-radius: 12px;
        color: #dedede;
        border: 1.5px solid #8b8b8b;
        background: #262626;
        padding: 12px 16px 16px 16px;
        align-items: flex-start;
        gap: 8px;
        text-overflow: ellipsis;
    }

    .input-enter::placeholder {
        overflow: hidden;
        color: #646464;
        font-family: Instrument Sans;
        font-size: 12px;
        font-style: normal;
        font-weight: 600;
        line-height: 125%;
    }

    .enter-links {
        display: flex;
        display: flex;
        justify-content: space-around;
        align-items: flex-start;
        align-self: stretch;
    }

    .enter-link {
        color: #cdeb09;
        font-family: Instrument Sans;
        font-size: 14px;
        font-style: normal;
        font-weight: 600;
        line-height: 130%;
        letter-spacing: 0.14px;
    }

    .link {
        text-decoration: none;
    }
    .call-to-action-btn {
        min-width: 100%;
        padding: 11px 30px 12px 30px;
        justify-content: center;
        border-radius: 100px;
        background: #cdeb09;
        border: none;
    }

    .call-to-action-text {
        color: #000;
        font-family: Instrument Sans;
        font-size: 18px;
        font-style: normal;
        font-weight: 550;
        line-height: 120%;
    }
</style>
